/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { nullCheckValue } from '../../services/shared/shared-constants';
import { SharedService } from '../../services/shared/shared.service';
import { AccountGrid, BusinessAccountRequestState, BusinessAccountsGridData, DealerLabels, UploadFileLabels } from './definitions/business-account-request.interface';
import { Address } from '../../services/shared/shared-interface';
import { AprRejBtnType, CtaList, GridValueData, HeaderObj, KngUploadFileState, ModalState } from 'kinetic-angular';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from '../../services/logger/logger.service';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Icons, ErrorMessage, BusinessAccountCategory, AltText, CssClass, CountryCode } from '../../services/shared/shared-enums';
@Injectable({
	providedIn: 'root'
})
export class BusinessAccountRequestUtilService 
{
	showActionBtn!: boolean;
	constructor(private sharedService: SharedService, private fb: FormBuilder, private loggerService: LoggerService, public toaster: ToastrService, private sharedUtilService: SharedUtilService) { }

	truncateFileName(fileName: string): string 
	{
		const maxLength = window.innerWidth >= 768 ? 10 : 12;
		const extensionIndex = fileName.lastIndexOf('.');
		if (extensionIndex > -1) 
		{
			const extension = fileName.substring(extensionIndex);
			const fileNameWithoutExtension = fileName.substring(0, extensionIndex);
			const truncatedName = fileNameWithoutExtension.length > maxLength ? `${fileNameWithoutExtension.substring(0, maxLength)}..` : fileNameWithoutExtension;
			return `${truncatedName}${extension}`;
		}
		return fileName;
	}
	checkIfValuePresent(value1: string, value2: string, value3: string) 
	{
		if ((value1 && value1.trim() !== '') || (value2 && value2.trim() !== '')) 
			return this.sharedService.emptyStringHandler(value1);
		this.sharedService.nullValueHandler(value3);
		return nullCheckValue;
	}
	setDefaultValues(obj: BusinessAccountsGridData) 
	{
		return {
			values: [],
			checked: false,
			id: obj.tempUserID,
			email: obj.profileInformation.email,
			state: '',
			portalUserId: '',
			company: obj.companyDetails.companyName ? obj.companyDetails.companyName : '',
			tempIsPrefilled: false,
			portalApprovedCustomerID: [],
			adminLocationID: obj.tempUserID,
			roles: [],
			phoneNumber: obj.profileInformation.phoneNumber,
			roleName: ''
		};
	}
	public updateHeaderObj(id: string, label: string) 
	{
		const isTooltip = (id === AccountGrid.businessAccountApproval);
		const headerObj: HeaderObj = { id: id, label: label, isTooltip: isTooltip };
		return headerObj;
	}
	formatAddress(obj: BusinessAccountsGridData) 
	{
		const address: Address = {
			addressLine1: obj.companyDetails.addressLine1,
			addressLine2: obj.companyDetails.addressLine2,
			city: obj.companyDetails.city,
			state: obj.companyDetails.state,
			zipCode: obj.companyDetails.country === CountryCode.canada || obj.companyDetails.country === CountryCode.canadaCountry ? obj.companyDetails.zipCode :
				this.formatZipcode(obj.companyDetails.zipCode)
		};
		return this.sharedService.formatAddress(address, true);
	}
	formatZipcode(value: string): string
	{
		let formattedZip = value;
		if(!(value.includes('-')))
		{
			const zipCode = value.replace(/[^\d]/g, '');
			const zipCodeLength = zipCode.length;
			formattedZip = zipCodeLength < 6 ? zipCode : `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
		}
		return formattedZip;
	}
	buildForm() 
	{
		return this.fb.group({
			accountName: [{ value: '', disabled: false }, [ Validators.required, (c: AbstractControl) => this.sharedService.alphaSpecialCharValidation(c, 'accountName') ] ],
			addressLine1: [{ value: '', disabled: false }, [ Validators.required, this.sharedService.characterValidationWithNumerics, this.sharedService.noWhitespaceValidator ] ],
			addressLine2: [{ value: '', disabled: false }, [this.sharedService.characterValidationWithNumerics] ],
			country: [{ value: '', disabled: false }, Validators.required ],
			city: [{ value: '', disabled: false }, [ Validators.required, (c: AbstractControl) => this.sharedService.alphaSpecialCharValidation(c, 'city') ] ],
			state: [{ value: '', disabled: false }, Validators.required ],
			postalCode: [{ value: '', disabled: false }, [ Validators.required, this.sharedService.zipCodeLengthCheck, this.sharedService.noWhitespaceValidator ] ]
		});
	}
	getCtaList(showActionBtn: boolean, acceptLabel: string, rejectLabel: string): CtaList[] 
	{
		return [
			{
				id: '',
				className: AprRejBtnType.singleApprove,
				state: {
					size: 'small',
					style: 'secondary',
					disabled: !showActionBtn,
					label: acceptLabel,
					icon: {
						altText: '',
						iconPath: !showActionBtn ? Icons.checkAcceptDisabled : Icons.approveIcon
					},
					position: 'left'
				}
			},
			{
				id: '',
				className: AprRejBtnType.singleReject,
				state: {
					size: 'small',
					style: 'secondary',
					disabled: !showActionBtn,
					label: rejectLabel,
					icon: {
						altText: '',
						iconPath: !showActionBtn ? Icons.closeRejectDisabled : Icons.rejectIcon
					},
					position: 'left'
				}
			}
		];
	}
	async handleError(error: HttpErrorResponse, selectedRowIndex: number, state: BusinessAccountRequestState)
	{
		const message = JSON.parse(await error.error.text()).message;
		this.toaster.error(message);
		this.locationLinkStatusChecked(false, selectedRowIndex, state);
	}
	getErrorResponse()
	{
		return {
			messages: [{ message: ErrorMessage.productionSupport, cssClass: CssClass.errorBusinessSection }],
			id: '',
			statusCode: 401,
			image: this.sharedUtilService.setImageBasedOnStatusCode(401)
		};
	}
	mapRemainAccessGridData(id: string, obj: BusinessAccountsGridData, valueListState: GridValueData, state: BusinessAccountRequestState) 
	{
		if (id === AccountGrid.locationList) 
		{
			valueListState.category = BusinessAccountCategory.uploadFile;
			valueListState.firstLineValue = this.truncateFileName(obj.pendingLocationDetails.updatedFileName);
			valueListState.icon = { iconPath: !obj.pendingLocationDetails.locationListReviewed ? Icons.uploadDisabled : Icons.uploadFile, altText: AltText.uploadFile };
			valueListState.thirdLineValue = state.sectionMiddle.grid.uploadFile;
		}
		if (id === AccountGrid.businessAccountApproval) 
		{
			valueListState.category = BusinessAccountCategory.button;
			valueListState.isTooltip = true;
			if(obj.pendingLocationDetails?.primaryGridOnly) 
			{
				this.showActionBtn = true;
			}
			else if(obj.pendingLocationDetails.locationListReviewed === true)
			{
				this.showActionBtn = true;
			}
			else 
			{
				this.showActionBtn = false;
			}
			valueListState.ctaList = this.getCtaList(this.showActionBtn, state.sectionMiddle.grid.accept.label, state.sectionMiddle.grid.reject.label);
		}
	}
	getHeaderLabel(id: string, state: BusinessAccountRequestState) 
	{
		const header = state.sectionMiddle.tableState.gridHeaders.find((header: HeaderObj) => header.id === id);
		return header ? header.label : '';
	}
	public updateAccessGridData(id: string, obj: BusinessAccountsGridData, state: BusinessAccountRequestState): GridValueData 
	{
		const valueListState: GridValueData = {
			label: this.getHeaderLabel(id, state),
			value: '',
			category: BusinessAccountCategory.primary,
			ctaList: [],
			className: ''
		};
		valueListState.label = this.getHeaderLabel(id, state);
		if (id === AccountGrid.requestedBy) 
		{
			valueListState.className = 'fw-bolder';
			if( obj.dealerInfo)
			{
				valueListState.category = BusinessAccountCategory.multiline ;
				valueListState.firstLineValue = DealerLabels.dealer;
				valueListState.secondLineValue = obj.dealerInfo.dealerName;
				valueListState.thirdLineValue = `${DealerLabels.id}: ${obj.dealerInfo.dealerId}` ;
			}
			else
			{
				valueListState.category = BusinessAccountCategory.primary;
				valueListState.value =  DealerLabels.user;
			}
		}
		else if (id === AccountGrid.requestedOn) 
		{
			valueListState.category = BusinessAccountCategory.date;
			valueListState.value =  obj.requestedOn;
		}
		else if (id === AccountGrid.topLevelGrid) 
		{
			valueListState.category = BusinessAccountCategory.primary;
			valueListState.value = this.sharedService.nullValueHandler(obj.topLevelGrid);
		}
		else if (id === AccountGrid.adminCompanyDetails) 
		{
			valueListState.category = BusinessAccountCategory.editData;
			valueListState.firstLineValue = obj.companyDetails.companyName;
			valueListState.secondLineValue = this.formatAddress(obj);
			valueListState.thirdLineValue = this.sharedService.formatUserName(state.sectionMiddle.grid.companyGridLabel, obj.companyDetails.primaryLocationGRID);
			valueListState.icon = { iconPath: Icons.editCompanyDetail, altText: AltText.editAdmingCompanyDetail };
			valueListState.firstLineValue = this.checkIfValuePresent(valueListState.firstLineValue, valueListState.secondLineValue, valueListState.thirdLineValue);
		}
		else if (id === AccountGrid.contact) 
		{
			valueListState.category = BusinessAccountCategory.multiline;
			const fullName = this.sharedService.formatUserName(obj.profileInformation.firstName, obj.profileInformation.lastName);
			valueListState.firstLineValue = (obj.dealerInfo ? `<b> ${fullName} </b>` : fullName ) + `${obj.pendingUsers?.length ? ` +${obj.pendingUsers.length} ${DealerLabels.users}` : ''}`;
			valueListState.secondLineValue = this.sharedService.emptyStringHandler(obj.profileInformation.email);
			valueListState.thirdLineValue = this.sharedService.formatPhoneNumber(obj.profileInformation.phoneNumber);
			valueListState.firstLineValue = this.checkIfValuePresent(valueListState.firstLineValue, valueListState.secondLineValue, valueListState.thirdLineValue);
		}
		this.mapRemainAccessGridData(id, obj, valueListState, state);
		return valueListState;
	}
	buttonStatusChecked(disabled: boolean, selectedRowIndex: number, state: BusinessAccountRequestState)
	{
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[5].ctaList[0].state.icon.iconPath = Icons.approveIcon;
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[5].ctaList[0].state.disabled = disabled;
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[5].ctaList[1].state.icon.iconPath = Icons.rejectIcon;
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[5].ctaList[1].state.disabled = disabled;
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[4].icon = { iconPath: disabled ? Icons.uploadDisabled : Icons.uploadFile, altText: AltText.uploadFile };
	}
	locationLinkStatusChecked(disabled: boolean, selectedRowIndex: number, state: BusinessAccountRequestState)
	{
		state.sectionMiddle.tableState.gridData[selectedRowIndex].values[4].disabled = disabled;
	}
	mapUploadFilePopup(data: UploadFileLabels, uploadFileState: KngUploadFileState)
	{
		uploadFileState.isLoading = false;
		uploadFileState.dragDropSection = data.uploadFile.dragDropLabel;
		uploadFileState.or = data.uploadFile.orLabel;
		uploadFileState.successMessage = data.uploadFile.success;
		uploadFileState.uploadButtons = data.uploadFile.uploadButtons;
		uploadFileState.errorImage.altText = data.uploadFile.errorIcon.altText;
		uploadFileState.successImage.altText = data.uploadFile.successIcon.altText;
		uploadFileState.dragDropImage.altText = data.uploadFile.onLoadIcon.altText;
		uploadFileState.errorImage.iconPath = Icons.actionRed;
		uploadFileState.successImage.iconPath = Icons.checkGreen;
		uploadFileState.dragDropImage.iconPath = Icons.dragDropIcon;
	}
	mapUploadModelState(data: UploadFileLabels, uploadModelState: ModalState)
	{
		uploadModelState.label = data.heading;
		uploadModelState.buttons[0].disabled = false;
		uploadModelState.buttons[0].label = data.modelButtons.cancel;
		uploadModelState.buttons[1].disabled = true;
		uploadModelState.buttons[1].label = data.modelButtons.upload;
	}
	resetUploadFileModelFlags(uploadFileState: KngUploadFileState)
	{
		uploadFileState.isSuccessClient = true;
		uploadFileState.isSuccessServer = false;
		uploadFileState.isError = false;
		uploadFileState.errorHeading = '';
		uploadFileState.errorSubHeading = '';
	}
}
